/**
 * CCO › Relatórios: relatório diário por unidade do Data & Analytics Agent (US-304 · RF-07) — Nexora Grid.
 *
 * Criado em: 18/10/2026 · Última modificação: 26/10/2026 · Versão: 1.2.0
 *
 * O agente gera, todo dia, o relatório do dia anterior lido só do hub: consumo de energia, água produzida e
 * distribuída, vazão média e máxima, kWh/m³, pressão mínima, nível médio e alarmes por unidade, com os destaques
 * (energia acima da semana, alarmes no dobro da média, pressão abaixo da NBR 12218). Hoje sai parcial, calculado na
 * hora. Exporta CSV para planilha.
 * v1.1 (US-3405): abaixo, os relatórios de semana, quinzena e mês (`RelatoriosPeriodo`).
 * v1.2 (US-3707): e o relatório regulatório da NR 9/ANA (`RelatorioNR9`).
 */
import { useEffect, useState } from "react";
import { api, baixar, ErroApi } from "../api";
import { useCco } from "./contexto";
import { RelatoriosPeriodo } from "./RelatoriosPeriodo";
import { RelatorioNR9 } from "./RelatorioNR9";

type Linha = {
  unidade: string; dispositivos: number; energia_kwh: number | null; agua_produzida_m3: number | null;
  agua_distribuida_m3: number | null; vazao_media_m3h: number | null; vazao_max_m3h: number | null; kwh_por_m3: number | null;
  pressao_min_mca: number | null; nivel_medio_pct: number | null; alarmes_alerta: number; alarmes_critico: number;
};
type Relatorio = {
  empresa: string; dia: string; horas: number; parcial: boolean; unidades: Linha[]; total: Partial<Linha>;
  destaques: { tipo: string; unidade: string; texto: string }[]; gerado_em: string; gerado_por: string;
};
type Resumo = { dia: string; gerado_em: string; destaques: number; total: Partial<Linha> };

const COLUNAS: [keyof Linha, string, number][] = [
  ["energia_kwh", "Energia (kWh)", 0], ["agua_produzida_m3", "Água produzida (m³)", 0], ["agua_distribuida_m3", "Água distribuída (m³)", 0],
  ["vazao_media_m3h", "Vazão média (m³/h)", 1], ["vazao_max_m3h", "Vazão máx. (m³/h)", 1], ["kwh_por_m3", "kWh/m³", 3],
  ["pressao_min_mca", "Pressão mín. (mca)", 1], ["nivel_medio_pct", "Nível médio (%)", 0],
];
const fmt = (v: number | null | undefined, casas: number) =>
  v === null || v === undefined ? "—" : v.toLocaleString("pt-BR", { maximumFractionDigits: casas, minimumFractionDigits: casas });
const dataBR = (iso: string) => iso.split("-").reverse().join("/");
const diaLocal = (deslocamentoDias = 0) =>
  new Date(Date.now() - deslocamentoDias * 86400000).toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" });

export function Relatorios() {
  const { empresa } = useCco();
  const [dia, setDia] = useState(diaLocal(1));
  const [relatorio, setRelatorio] = useState<Relatorio | null>(null);
  const [lista, setLista] = useState<Resumo[]>([]);
  const [estado, setEstado] = useState("");

  useEffect(() => {
    if (!empresa) return;
    api<Resumo[]>(`/v1/relatorios/diarios?empresa=${empresa}`).then(setLista).catch(() => setLista([]));
  }, [empresa]);
  useEffect(() => {
    if (!empresa) return;
    setEstado("carregando o relatório…");
    api<Relatorio>(`/v1/relatorios/diario?empresa=${empresa}&dia=${dia}`)
      .then((r) => { setRelatorio(r); setEstado(""); })
      .catch((e) => { setRelatorio(null); setEstado(e instanceof ErroApi ? e.message : "relatório indisponível"); });
  }, [empresa, dia]);

  const baixarCsv = () => {
    if (!empresa) return;
    baixar(`/v1/relatorios/diario?empresa=${empresa}&dia=${dia}&formato=csv`, `relatorio_${empresa}_${dia}.csv`)
      .catch(() => setEstado("falha ao baixar o CSV"));
  };

  return (
    <section className="tela-relatorios" aria-label="Relatórios">
      <div className="linha-relatorio">
        <h2>Relatório diário por unidade</h2>
        <label>Dia <input type="date" value={dia} max={diaLocal(0)} onChange={(e) => e.target.value && setDia(e.target.value)} /></label>
        {relatorio && <button type="button" className="nx-botao" onClick={baixarCsv}>Baixar CSV</button>}
      </div>
      {estado && <p className="nx-suave">{estado}</p>}
      {relatorio && (
        <>
          <p className="nx-suave">
            {dataBR(relatorio.dia)} · {relatorio.parcial ? `parcial (${relatorio.horas.toLocaleString("pt-BR")} h decorridas)` : "dia fechado"} ·
            gerado por {relatorio.gerado_por === "agente" ? "Data & Analytics Agent" : relatorio.gerado_por} em{" "}
            {new Date(relatorio.gerado_em).toLocaleString("pt-BR")} · dados do hub da operadora
          </p>
          <div className="cartoes-relatorio">
            <div className="nx-cartao"><span className="nx-suave">Energia</span><b className="nx-valor">{fmt(relatorio.total.energia_kwh, 0)} kWh</b></div>
            <div className="nx-cartao"><span className="nx-suave">Água produzida</span><b className="nx-valor">{fmt(relatorio.total.agua_produzida_m3, 0)} m³</b></div>
            <div className="nx-cartao"><span className="nx-suave">Água distribuída</span><b className="nx-valor">{fmt(relatorio.total.agua_distribuida_m3, 0)} m³</b></div>
            <div className="nx-cartao"><span className="nx-suave">Eficiência</span><b className="nx-valor">{fmt(relatorio.total.kwh_por_m3, 3)} kWh/m³</b></div>
            <div className="nx-cartao"><span className="nx-suave">Alarmes</span>
              <b className="nx-valor">{relatorio.total.alarmes_critico ?? 0} críticos · {relatorio.total.alarmes_alerta ?? 0} alertas</b></div>
          </div>
          {relatorio.destaques.length > 0 && (
            <div className="nx-cartao destaques-relatorio" role="status">
              <h3>Destaques</h3>
              <ul>{relatorio.destaques.map((d, i) => <li key={i}>{d.texto}</li>)}</ul>
            </div>
          )}
          <div className="tabela-rolavel">
            <table className="tabela-relatorio">
              <thead>
                <tr><th>Unidade</th><th>Disp.</th>{COLUNAS.map(([, t]) => <th key={t}>{t}</th>)}<th>Críticos</th><th>Alertas</th></tr>
              </thead>
              <tbody>
                {relatorio.unidades.map((l) => (
                  <tr key={l.unidade}>
                    <td>{l.unidade}</td><td>{l.dispositivos}</td>
                    {COLUNAS.map(([c, , casas]) => (
                      <td key={c} className={c === "pressao_min_mca" && (l.pressao_min_mca ?? 99) < 10 ? "prioridade-2" : ""}>{fmt(l[c] as number | null, casas)}</td>
                    ))}
                    <td>{l.alarmes_critico}</td><td>{l.alarmes_alerta}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {lista.length > 0 && (
        <details className="historico-relatorios">
          <summary>Relatórios guardados ({lista.length})</summary>
          <ul>
            {lista.map((r) => (
              <li key={r.dia}>
                <button type="button" className="nx-botao" onClick={() => setDia(r.dia)}>{dataBR(r.dia)}</button>
                <span className="nx-suave"> {fmt(r.total.energia_kwh, 0)} kWh · {r.total.alarmes_critico ?? 0} críticos · {r.destaques} destaque(s)</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      <RelatoriosPeriodo />
      <RelatorioNR9 />
    </section>
  );
}
